import { motion } from "framer-motion";
import { FlaskConical, Target, Shield } from "lucide-react";

const features = [
  {
    icon: FlaskConical,
    title: "Single Active Compound",
    description: "Creatine Monohydrate is the most researched performance compound in sport.",
  },
  {
    icon: Target,
    title: "No Blends",
    description: "No delivery agents. No enzymatic modifiers. Pure creatine only.",
  },
  {
    icon: Shield,
    title: "Proven Effective",
    description: "Proven effective through direct cellular saturation.",
  },
];

export const WhyCreatine = () => {
  return (
    <section 
      className="py-24 md:py-32 bg-[#0A0A0A]"
      data-testid="why-creatine-section"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24">
        {/* Section header */}
        <div className="mb-16">
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="font-mono text-sm tracking-[0.3em] text-[#E82D00] mb-4"
          >
            WHY CREATINE MONOHYDRATE
          </motion.p>
          
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="text-4xl md:text-5xl font-bold uppercase tracking-tight text-white"
            style={{ fontFamily: 'Oswald, sans-serif' }}
            data-testid="why-heading"
          >
            Single Active Compound
          </motion.h2>
        </div>

        {/* Feature cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="benefit-card group"
              data-testid={`why-card-${index}`}
            >
              {/* Icon */}
              <div className="icon-container mb-6 group-hover:border-[#616556] transition-colors duration-300">
                <feature.icon size={24} strokeWidth={1.5} className="text-[#E82D00]" />
              </div>

              {/* Title */}
              <h3 
                className="text-xl font-bold uppercase tracking-tight text-white mb-4"
                style={{ fontFamily: 'Oswald, sans-serif' }}
              >
                {feature.title}
              </h3>

              {/* Description */}
              <p className="text-[#A3A3A3] leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Bottom highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="mt-16 p-8 border border-[#616556]/30 bg-[#050505]"
          data-testid="why-highlight"
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 text-center">
            <div>
              <p className="font-mono text-xs tracking-widest text-[#A3A3A3] mb-2">NO BLENDS</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>100% PURE</p>
            </div>
            <div>
              <p className="font-mono text-xs tracking-widest text-[#A3A3A3] mb-2">NO DELIVERY AGENTS</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>DIRECT ABSORPTION</p>
            </div>
            <div>
              <p className="font-mono text-xs tracking-widest text-[#A3A3A3] mb-2">NO MODIFIERS</p>
              <p className="text-2xl font-bold text-white" style={{ fontFamily: 'Oswald, sans-serif' }}>CELLULAR SATURATION</p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyCreatine;
